import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule'
import Region from '@/models/Settings/Regions/Region';
import PublicHoliday from '@/models/Settings/Regions/PublicHoliday';
import CreateRegionRequest from '@/models/Settings/Regions/CreateRegionRequest';
import UpdateRegionRequest from '@/models/Settings/Regions/UpdateRegionRequest';
import CreatePublicHolidayRequest from '@/models/Settings/Regions/CreatePublicHolidayRequest';
import UpdatePublicHolidayRequest from '@/models/Settings/Regions/UpdatePublicHolidayRequest';
import HistoryEntry from '@/models/Shared/HistoryEntry';
import HistoryUtils from '@/lib/HistoryUtils';
import GetHistoryResponse from '@/models/Shared/GetHistoryResponse';
import PermissionUtils from '@/lib/permissionUtils';

export interface IRegionsState {
    loading: boolean;
    loadingPublicHolidays: boolean;
    loadingUpcomingPublicHolidays: boolean;
    regions: Region[];
    totalRegionsPages: number;
    region: Region;
    newRegionId: string;
    publicHolidays: PublicHoliday[];
    upcomingPublicHolidays: PublicHoliday[];
    currentPublicHoliday: PublicHoliday;
    totalPublicHolidaysPages: number;
    loadingHistory: boolean;
    history: HistoryEntry[];
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'RegionsModule', namespaced: true })
class RegionsStore extends VuexModule implements IRegionsState {

    public loading: boolean = true;
    public loadingPublicHolidays: boolean = false;
    public loadingUpcomingPublicHolidays: boolean = false;
    public regions: Region[] = [];
    public totalRegionsPages: number = 1;
    public region: Region = new Region();
    public newRegionId: string = null;
    public publicHolidays: PublicHoliday[] = [];
    public upcomingPublicHolidays: PublicHoliday[] = [];
    public currentPublicHoliday: PublicHoliday = new PublicHoliday;
    public totalPublicHolidaysPages: number = 1;
    public loadingHistory: boolean = false;
    public history: HistoryEntry[] = [];
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getRegions(pageNumber: number) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/regions?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setRegions", response.data.results);
            this.context.commit("setRegionsTotalPages", response.data.records.totalRecords);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getRegion(regionId: string) {

        this.setLoading(true);

        await ajax.get(`/portal-backend/fi-api/internal/regions/${regionId}`)
        .then(response => {
            this.context.commit("setRegion", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async createRegion(request: CreateRegionRequest) {

        this.setLoading(true);

        await ajax.post(`/portal-backend/fi-api/internal/regions/`, request)
        .then(response => {
            this.context.commit("setRegionFromCreate", response.data.id);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async editRegion(request: UpdateRegionRequest) {

        this.setLoading(true);

        await ajax.patch(`/portal-backend/fi-api/internal/regions/${this.region.id}`, request)
        .then(response => {
            this.context.commit("setRegion", response.data);
            this.setLoading(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoading(false);
        });
    }

    @Action
    async getPublicHolidays(regionId: string) {

        this.setLoadingPublicHolidays(true);

        await ajax.get(`/portal-backend/fi-api/internal/holidays?regionId=${regionId}`)
        .then(response => {
            this.context.commit("setPublicHolidays", response.data.results);
            this.context.commit("setPublicHolidaysTotalPages", response.data.records.totalRecords);
            if (response.data.results.length > 0) {
                this.context.commit("setCurrentPublicHoliday", response.data.results[0]);
            }
            this.setLoadingPublicHolidays(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPublicHolidays(false);
        });
    }

    @Action
    async getUpcomingPublicHolidays(upcomingHolidayCount: number) {

        this.setLoadingUpcomingPublicHolidays(true);

        // Gets all of the regions without pagination first, then works out the default
        // region and makes a second call to go and get the public holidays
        await ajax.get(`/portal-backend/fi-api/internal/regions`)
        .then(response => {

            const allRegions = response.data.results;
            const defaultRegion = allRegions.find((region: Region) => { return region.default; });

            if (defaultRegion) {
                // If a default  region exists then get all public holidays for the region
                ajax.get(`/portal-backend/fi-api/internal/holidays?regionId=${defaultRegion.id}`)
                .then(response => {

                    const allPublicHolidays = response.data.results;

                    // Work out the upcoming public holidays from the current date by looping
                    // through all public holidays until we have enough that have a holidayDate after now
                    let upcomingPublicHolidays = [];
                    let now = new Date();
                    let count = 0;
                    while ((upcomingPublicHolidays.length < upcomingHolidayCount) && (count < allPublicHolidays.length)) {
                        const holidayDate = new Date(allPublicHolidays[count].holidayDate);
                        if (now <= holidayDate) {
                            upcomingPublicHolidays.push(allPublicHolidays[count]);
                        }
                        count++;
                    }

                    // Set the upcoming public holidays
                    this.context.commit("setUpcomingPublicHolidays", upcomingPublicHolidays);
                    this.setLoadingUpcomingPublicHolidays(false);

                })
                .catch(error => {
                    AppModule.handleApiError(error);
                    this.setLoadingUpcomingPublicHolidays(false);
                });
            }
            else {
                // Else stop loading and leave the public holidays empty, this is to prevent null pointers
                this.setLoadingUpcomingPublicHolidays(false);
            }
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingUpcomingPublicHolidays(false);
        });
    }

    @Action
    public async updateCurrentPublicHoliday(publicHoliday: PublicHoliday){
        this.context.commit("setCurrentPublicHoliday", publicHoliday);
    }

    @Action
    async createPublicHoliday(request: CreatePublicHolidayRequest) {

        this.setLoadingPublicHolidays(true);

        await ajax.post(`/portal-backend/fi-api/internal/holidays/`, request)
        .then(response => {
            this.context.commit("setCurrentPublicHoliday", response.data);
            this.setLoadingPublicHolidays(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPublicHolidays(false);
        });
    }

    @Action
    async editPublicHoliday(request: UpdatePublicHolidayRequest) {

        this.setLoadingPublicHolidays(true);

        await ajax.patch(`/portal-backend/fi-api/internal/holidays/${this.currentPublicHoliday.id}`, request)
        .then(response => {
            this.context.commit("setCurrentPublicHoliday", response.data);
            this.setLoadingPublicHolidays(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPublicHolidays(false);
        });
    }

    @Action
    async deletePublicHoliday() {

        this.setLoadingPublicHolidays(true);

        await ajax.delete(`/portal-backend/fi-api/internal/holidays/${this.currentPublicHoliday.id}`)
        .then(() => {
            this.setLoadingPublicHolidays(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPublicHolidays(false);
        });
    }

    @Action
    async getHistory() {

        this.setLoadingHistory(true);

        await ajax.get(`/portal-backend/fi-api/internal/regions/${this.region.id}/history`)
        .then(response => {
            this.context.commit("setHistory", response.data);
            this.setLoadingHistory(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingHistory(false);
        });
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loading = true;
        this.loadingPublicHolidays = false;
        this.loadingUpcomingPublicHolidays = false;
        this.regions = [];
        this.totalRegionsPages = 1;
        this.publicHolidays = [];
        this.upcomingPublicHolidays = [];
        this.currentPublicHoliday = new PublicHoliday();
        this.totalPublicHolidaysPages = 1;
        this.region = null;
    }

    @Mutation
    private async setLoading(loading: boolean) {
        this.loading = loading;
    }

    @Mutation
    private async setLoadingPublicHolidays(loading: boolean) {
        this.loadingPublicHolidays = loading;
    }

    @Mutation
    private async setLoadingUpcomingPublicHolidays(loading: boolean) {
        this.loadingUpcomingPublicHolidays = loading;
    }

    @Mutation
    private async setRegions(response: Region[]) {
        if (response) {
            this.regions = response;
        }
    }

    @Mutation
    private async setRegionsTotalPages(total: number) {
        this.totalRegionsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setRegion(response: Region) {
        this.region = response;
    }

    @Mutation
    private async setRegionFromCreate(regionId: string) {
        this.newRegionId = regionId;
    }

    @Mutation
    private async setPublicHolidays(response: PublicHoliday[]) {
        if (response) {
            this.publicHolidays = response;
        }
    }

    @Mutation
    private async setUpcomingPublicHolidays(response: PublicHoliday[]) {
        if (response) {
            this.upcomingPublicHolidays = response;
        }
    }

    @Mutation
    private async setCurrentPublicHoliday(response: PublicHoliday) {
        this.currentPublicHoliday = response;
    }

    @Mutation
    private async setPublicHolidaysTotalPages(total: number) {
        this.totalPublicHolidaysPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setLoadingHistory(loading: boolean) {
        this.loadingHistory = loading;
    }

    @Mutation
    private async setHistory(response: GetHistoryResponse) {
        const history = HistoryUtils.cleanHistory(response);
        this.history = history;
    }

}

export const RegionsModule = getModule(RegionsStore);